import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import parse from 'html-react-parser';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Tag,
  BookOpen,
  Share2,
  ChevronUp
} from 'lucide-react';
import { postService } from '../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const PostView = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [fontSize, setFontSize] = useState('medium'); // small, medium, large
  const [readingMode, setReadingMode] = useState(false);
  const [isHtmlContent, setIsHtmlContent] = useState(false);

  // Calculate reading time
  const calculateReadingTime = (text) => {
    const wordsPerMinute = 200;
    const words = text.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };

  // Handle scroll for reading progress
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.pageYOffset / totalHeight) * 100;
      setReadingProgress(Math.min(progress, 100));
      setShowScrollToTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Carregar tamanho de fonte preferido
  useEffect(() => {
    const savedFontSize = localStorage.getItem('preferred-font-size');
    if (savedFontSize) {
      setFontSize(savedFontSize);
    }
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Funções para controle de leitura
  const changeFontSize = (size) => {
    setFontSize(size);
    localStorage.setItem('preferred-font-size', size);
  };

  const toggleReadingMode = () => {
    setReadingMode(prev => !prev);
  };

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await postService.getPostBySlug(slug);

        if (response.success) {
          const postData = response.data.post;

          // Detectar se o conteúdo é HTML
          const htmlTagsRegex = /<\/?[a-z][^>]*>/gi;
          const htmlTagsCount = (postData.content.match(htmlTagsRegex) || []).length;
          const isHtml = htmlTagsCount > 5;

          setIsHtmlContent(isHtml);
          setPost(postData);
        } else {
          setError(response.message || 'Post não encontrado');
        }
      } catch (error) {
        console.error('Erro ao carregar post:', error);
        setError('Erro ao carregar o post');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadPost();
    }
  }, [slug]);

  // Definir o título da página quando o post estiver carregado
  useEffect(() => {
    if (post && document.title !== `${post.title} | Igreja de Jardim`) {
      document.title = `${post.title} | Igreja de Jardim`;
    }
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-church-green mx-auto"></div>
          <p className="text-gray-400">Carregando estudo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-black">
        <Card className="max-w-md w-full text-center bg-[#0a0a0a] border-white/10">
          <CardContent className="pt-6">
            <div className="text-4xl mb-4">📖</div>
            <h2 className="text-xl font-heading text-white mb-2">Ops! Algo deu errado</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white hover:text-black">
              <Link to="/">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar ao início
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-black">
        <Card className="max-w-md w-full text-center bg-[#0a0a0a] border-white/10">
          <CardContent className="pt-6">
            <div className="text-4xl mb-4">📝</div>
            <h2 className="text-xl font-heading text-white mb-2">Estudo não encontrado</h2>
            <p className="text-gray-400 mb-4">
              O estudo que você está procurando não existe ou foi removido.
            </p>
            <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white hover:text-black">
              <Link to="/">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar ao início
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const readingTime = calculateReadingTime(post.content);

  return (
    <>
      <PageTitle
        title={post.title}
        description={post.excerpt || `Estudo bíblico sobre ${post.title}`}
      />
      {/* Reading Progress Bar */}
      <div
        className="fixed top-0 left-0 h-1 bg-church-green z-50 transition-all duration-150 ease-out"
        style={{ width: `${readingProgress}%` }}
      />

      {/* Main Content */}
      <div className={`min-h-screen transition-all duration-300 bg-black text-gray-200`}>
        {/* Navigation */}
        <div className={`sticky top-0 z-40 border-b transition-all duration-300 ${readingMode ? 'bg-black/95 border-b-0 shadow-lg' : 'bg-black/80 backdrop-blur-md border-white/10'}`}>
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <Button variant="ghost" size="sm" asChild className="text-gray-400 hover:text-white hover:bg-white/5">
              <Link to="/">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {!readingMode && "Voltar aos estudos"}
              </Link>
            </Button>

            {/* Reading controls */}
            <div className="flex items-center gap-3">
              {/* Font size controls */}
              <div className="flex items-center border border-white/10 rounded-md overflow-hidden">
                <button
                  onClick={() => changeFontSize('small')}
                  className={`px-3 py-1.5 text-xs transition-colors ${fontSize === 'small' ? 'bg-church-green text-white' : 'text-gray-400 hover:bg-white/5'}`}
                  title="Fonte pequena"
                >
                  A
                </button>
                <button
                  onClick={() => changeFontSize('medium')}
                  className={`px-3 py-1.5 text-sm transition-colors ${fontSize === 'medium' ? 'bg-church-green text-white' : 'text-gray-400 hover:bg-white/5'}`}
                  title="Fonte média"
                >
                  A
                </button>
                <button
                  onClick={() => changeFontSize('large')}
                  className={`px-3 py-1.5 text-base transition-colors ${fontSize === 'large' ? 'bg-church-green text-white' : 'text-gray-400 hover:bg-white/5'}`}
                  title="Fonte grande"
                >
                  A
                </button>
              </div>

              {/* Reading mode toggle */}
              <Button
                variant={readingMode ? "default" : "outline"}
                size="sm"
                onClick={toggleReadingMode}
                title={readingMode ? "Desativar modo de leitura" : "Ativar modo de leitura"}
                className={readingMode ? "bg-church-green text-white border-transparent" : "border-white/20 text-gray-400 hover:text-white hover:bg-white/5"}
              >
                <BookOpen className="h-4 w-4 mr-2" />
                {readingMode ? "Focado" : "Leitura"}
              </Button>
            </div>
          </div>
        </div>

        {/* Article Container */}
        <article className={`container mx-auto px-4 pb-20 pt-10 transition-all duration-300 ${readingMode ? 'max-w-3xl' : ''}`}>
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <header className="mb-16 text-center animate-fade-in">
              <div className="mb-10 space-y-6">
                <h1 className="text-4xl lg:text-5xl xl:text-6xl font-heading font-bold text-white leading-tight tracking-tight">
                  {post.title}
                </h1>

                {post.excerpt && (
                  <div className="max-w-2xl mx-auto">
                    <p className="text-xl text-gray-400 leading-relaxed font-sans font-light italic">
                      {post.excerpt}
                    </p>
                  </div>
                )}
              </div>

              {/* Meta Information */}
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500 mb-12 uppercase tracking-wider font-medium">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span className="text-gray-300">{post.author?.name || 'Igreja de Jardim'}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{format(new Date(post.createdAt), "d 'de' MMM 'de' yyyy", { locale: ptBR })}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{readingTime} min</span>
                </div>
              </div>

              {/* Featured Image */}
              {post.featuredImage && (
                <div className="mb-16 animate-fade-in animation-delay-200">
                  <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-church-green/5 border border-white/5">
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="w-full h-auto max-h-[600px] object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
            </header>

            {/* Content */}
            <div className={`prose prose-invert max-w-none transition-all duration-300
              ${fontSize === 'small' ? 'prose-sm' : fontSize === 'large' ? 'prose-xl' : 'prose-lg'}
              ${readingMode ? 'leading-loose tracking-wide' : 'leading-relaxed'}
              prose-headings:font-heading prose-headings:font-bold prose-headings:text-white
              prose-h1:text-center
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-2 prose-h2:border-l-4 prose-h2:border-church-green prose-h2:pl-4
              prose-p:text-gray-300 prose-p:font-sans
              prose-a:text-church-green prose-a:no-underline hover:prose-a:underline
              prose-strong:text-white prose-strong:font-semibold
              prose-blockquote:border-l-4 prose-blockquote:border-church-green prose-blockquote:bg-white/5 
              prose-blockquote:rounded-r-lg prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:my-8
              prose-blockquote:text-gray-200 prose-blockquote:italic
              prose-code:bg-white/10 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-church-green
              prose-img:rounded-xl prose-img:shadow-lg prose-img:border prose-img:border-white/5
              font-sans`}
            >
              {isHtmlContent ? (
                <div className="html-content">
                  {parse(post.content)}
                </div>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ node, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer">{props.children}</a>
                    ),
                    img: ({ node, ...props }) => (
                      <div className="my-10">
                        <img {...props} loading="lazy" className="mx-auto rounded-xl shadow-lg border border-white/5" alt={props.alt || 'Imagem do estudo'} />
                        {props.alt && <figcaption className="text-center text-gray-500 text-sm mt-3 italic">{props.alt}</figcaption>}
                      </div>
                    )
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              )}
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-20 pt-10 border-t border-white/10">
                <div className="flex items-center gap-3 mb-6">
                  <Tag className="h-5 w-5 text-church-green" />
                  <span className="font-heading font-bold text-white text-lg">Tags relacionadas</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {post.tags.map(tag => (
                    <Badge
                      key={tag.id}
                      variant="secondary"
                      className="text-sm py-1.5 px-4 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                      style={{
                        border: `1px solid ${tag.color || '#333'}`,
                        color: tag.color || '#fff'
                      }}
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Footer Actions */}
            <footer className="mt-16 pt-10 border-t border-white/10">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <Button variant="outline" size="lg" asChild className="border-white/20 text-white hover:bg-white hover:text-black transition-colors w-full sm:w-auto">
                  <Link to="/">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Voltar aos estudos
                  </Link>
                </Button>

                <div className="flex items-center gap-2 w-full sm:w-auto justify-center">
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                    <Share2 className="h-4 w-4 mr-2" />
                    Compartilhar
                  </Button>
                </div>
              </div>
            </footer>
          </div>
        </article>
      </div>

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <Button
          size="icon"
          className="fixed bottom-8 right-8 z-40 shadow-xl bg-church-green hover:bg-church-green/80 text-white rounded-full h-12 w-12"
          onClick={scrollToTop}
        >
          <ChevronUp className="h-5 w-5" />
        </Button>
      )}
    </>
  );
};

export default PostView;